import { Badge, Group, Loader, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { listConversations } from '../api/gen';
import type { Conversation } from '../api/gen';
import { listQuery, where, type Filter } from '../api/list';
import { useOrg } from '../api/org';
import { since, when } from '../lib/format';

/** The saved filters an operator actually works from. */
export type View = 'waiting' | 'open' | 'mine' | 'snoozed' | 'closed';

export const VIEWS: { value: View; label: string }[] = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'open', label: 'Open' },
  { value: 'mine', label: 'Mine' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'closed', label: 'Closed' },
];

// Each view is a filter over the one list endpoint, built through the typed
// helper: a property the server does not offer, or an operator it does not
// take on that property, is a type error here rather than a 400 in production.
function filterFor(view: View, memberId: string | undefined): Filter<'conversations'>[] {
  switch (view) {
    // Waiting is the customer's clock, not the thread's status: an open
    // thread whose customer has an unanswered message. Open includes the ones
    // already answered and waiting on the customer.
    case 'waiting':
      return [
        where('conversations', 'status', 'eq', 'open'),
        where('conversations', 'waiting', 'eq', true),
      ];
    case 'open':
      return [where('conversations', 'status', 'eq', 'open')];
    case 'mine':
      return memberId
        ? [where('conversations', 'assigneeId', 'eq', memberId)]
        : [where('conversations', 'assignee', 'eq', 'member')];
    case 'snoozed':
      return [where('conversations', 'status', 'eq', 'snoozed')];
    case 'closed':
      return [where('conversations', 'status', 'eq', 'closed')];
  }
}

export function ConversationList({
  view,
  search,
  selected,
  onSelect,
  memberId,
}: {
  view: View;
  search: string;
  selected: string | null;
  onSelect: (id: string) => void;
  memberId?: string;
}) {
  const org = useOrg();
  const { data, isLoading } = useQuery({
    queryKey: ['conversations', view, search, memberId],
    queryFn: () =>
      unwrap(
        listConversations({
          headers: org,
          query: listQuery('conversations', {
            where: filterFor(view, memberId),
            search: search || undefined,
            // The waiting view sorts by who has been waiting longest, which is
            // the whole reason it exists. Everything else sorts by what moved
            // last.
            sort: view === 'waiting' ? 'awaitingSince' : 'lastMessageAt',
            sortDir: view === 'waiting' ? 'asc' : 'desc',
            limit: 100,
          }),
        }),
      ),
  });

  const rows = data?.data ?? [];

  if (isLoading) {
    return (
      <Group justify="center" p="md">
        <Loader size="sm" />
      </Group>
    );
  }
  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed" p="md">
        Nothing here.
      </Text>
    );
  }

  return (
    <ScrollArea h="100%">
      <Stack gap={0}>
        {rows.map((c) => (
          <Row key={c.id} conversation={c} selected={c.id === selected} onSelect={onSelect} />
        ))}
      </Stack>
    </ScrollArea>
  );
}

function Row({
  conversation,
  selected,
  onSelect,
}: {
  conversation: Conversation;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <UnstyledButton
      onClick={() => onSelect(conversation.id)}
      p="sm"
      style={(theme) => ({
        borderBottom: `1px solid ${theme.colors.gray[3]}`,
        background: selected ? theme.colors.blue[0] : undefined,
      })}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Text fw={600} size="sm" lineClamp={1}>
          {conversation.subject || 'No subject'}
        </Text>
        {conversation.awaitingSince ? (
          <Badge size="xs" color="orange" variant="light">
            {since(conversation.awaitingSince)}
          </Badge>
        ) : null}
      </Group>
      <Group gap={6} mt={4}>
        <Badge size="xs" variant="default">
          {conversation.channel}
        </Badge>
        {conversation.assignee === 'agent' ? (
          <Badge size="xs" color="grape" variant="light">
            agent
          </Badge>
        ) : null}
        {conversation.status === 'snoozed' ? (
          <Badge size="xs" color="gray" variant="light">
            {conversation.snoozedUntil ? `snoozed until ${when(conversation.snoozedUntil)}` : 'snoozed'}
          </Badge>
        ) : null}
        {conversation.tags.slice(0, 2).map((t) => (
          <Badge key={t} size="xs" variant="outline">
            {t}
          </Badge>
        ))}
      </Group>
    </UnstyledButton>
  );
}
